// Turns a thrown mutation error (from the unified set-role / remove-member
// hooks) into the message we want users to read. The server returns
// HTTP 409 with `code === 'groups/cannot_demote_last_leader'` or
// `'groups/cannot_remove_last_leader'` when the action would leave the
// group with no leader; both surface a "promote someone else first"
// nudge. Everything else falls back to the wire envelope message — or
// the supplied fallback when the wire didn't carry one.

import { GROUP_MEMBER_ERROR_CODES } from '../../lib/mutations/groups';
import { parseApiErrorCode, parseApiErrorMessage } from '../../lib/api-error';

const LAST_LEADER_NUDGE_EN =
  'This is the only leader — promote another member first.';

export function formatGroupMemberError(
  err: unknown,
  fallback: string,
  lastLeaderNudge: string = LAST_LEADER_NUDGE_EN,
): string {
  // Wire envelope first (most server-thrown errors arrive in this shape);
  // fall back to the top-level `{ code, message }` shape that in-process
  // `DomainError` throws produce.
  const envelopeCode = parseApiErrorCode(err);
  const topLevelCode =
    err !== null && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string'
      ? (err as { code: string }).code
      : null;
  const code = envelopeCode ?? topLevelCode;

  if (
    code === GROUP_MEMBER_ERROR_CODES.cannotDemoteLastLeader ||
    code === GROUP_MEMBER_ERROR_CODES.cannotRemoveLastLeader
  ) {
    return lastLeaderNudge;
  }

  const envelopeMessage = parseApiErrorMessage(err);
  if (envelopeMessage !== null) return envelopeMessage;
  if (err !== null && typeof err === 'object') {
    const topLevelMessage = (err as { message?: unknown }).message;
    if (typeof topLevelMessage === 'string') return topLevelMessage;
  }
  return fallback;
}
